import React, { useMemo } from 'react';

import Conditional from '../../../common/conditional';
import CustomNewItem from '../../custom-general/custom-new-item';
import ChecklistForm from '../checklist/checklistItem';
import ExistingStructureItem from '../existing-structure-item';
import FVIStructurePIDImageForm from '../../../forms/custom-fvi/fviStructurePIDImageForm';
import FVIStructureAnomalyLocations from '../../../forms/custom-fvi/fviStructureAnomalyLocations';
import FVIPDOChecklistForm from '../visual-inspection-pdo/fvi-pdo-checklist-form';
import ItemCount from '../../../blocks/item-count';
import PDOInclusionForm from '../../../forms/custom-fvi/pdoInclusionForm';
import StructuralMemberForm from '../../../forms/custom-fvi/structuralMemberForm';
import VIPhotoRecordForm from '../../../forms/visual-inspection/viPhotoRecordForm';
import WarningNotice from '../warning-notice';
import Zippy from '../../../themed/zippy';

import { matchPhrases, getAnomalyStartNumber, getSomeChecklistItemsMissing } from '../../../../utils/utilities';
import { MAX_INSPECTED_ITEMS_PER_REPORT  } from '../../../../utils/constants';
import { AnomalyLocationData, ChecklistItem, ConcreteHammerData, FVIPDOChecklistItem, FVIStructureItem, NewAnomalyItem, PDFImageProps, PIDData, StructureInspectionTypeEntry, StructureUTMData } from '../../../../utils/models';

import useAnomalyRecords from '../../../../hooks/useAnomalyRecords';
import useIncompleteInspectedSections from '../../../../hooks/useIncompleteInspectedSections';
import useInspectedItemIDUpdate from '../../../../hooks/useInspectedItemIDUpdate';
import useMappedAnomalies from '../../../../hooks/useMappedAnomalies';

import toastService from '../../../../services/toastService';

interface Props {
    checklistItems: ChecklistItem[];
    checklistLabel: string;
    additionalComment: string;
    hasPDO: string;
    onChangeHasPDO: (hasPDO: string) => void;
    generalPhotos: PDFImageProps[];
    pdoChecklistItems: FVIPDOChecklistItem[];
    onChangePDOChecklistItems: (pdoChecklistItems: FVIPDOChecklistItem[]) => void;
    onChangeGeneralPhotos: (photos: PDFImageProps[]) => void;
    onChangeChecklistItems: (checklistItems: ChecklistItem[]) => void;
    onChangeAdditionalComment: (comment: string) => void;
    anomalyLocationData: AnomalyLocationData;
    onChangeAnomalyLocationData: (anomalyLocationData: AnomalyLocationData) => void;
    pidData: PIDData;
    onChangePIDData: (pidData: PIDData) => void;
    items: FVIStructureItem[];
    onItemsChange: (items: FVIStructureItem[]) => void;
    enableComponentInspectionDetails?: boolean;
}

const StructureItems: React.FC<Props> = ({ additionalComment, anomalyLocationData, onChangeAnomalyLocationData, pdoChecklistItems, onChangePDOChecklistItems, generalPhotos, onChangeGeneralPhotos, hasPDO, onChangeHasPDO, checklistItems, checklistLabel, onChangeAdditionalComment, onChangeChecklistItems, pidData, onChangePIDData, items: structureItems,  onItemsChange, enableComponentInspectionDetails = false }) => {
    const { incompleteSections, isLastInspectedItemComplete } = useIncompleteInspectedSections(structureItems)
    
    const handleAddStructureItem = (data: Partial<FVIStructureItem>) => {
        const matchFound = structureItems.find(({ structureMemberName }) => matchPhrases(structureMemberName, data.structureMemberName));
        if (matchFound) return toastService.error(`${data.structureMemberName} has already been added!`);

        const newStructureItem: FVIStructureItem = {
            id: structureItems.length + 1,
            structureMemberName: data.structureMemberName!,
            anomalyItems: [],
            hasAnomalies: data.hasAnomalies!,
            inspectionDescription: data.inspectionDescription!,
            generalDrawing: [],
            generalPhotos: [],
            ...(enableComponentInspectionDetails ? { location: data.location ?? '', inspectionTypeEntries: [] } : {}),
        };

        const newStructureItems: FVIStructureItem[] = [...structureItems, newStructureItem];
        onItemsChange(newStructureItems);
    };

    const handleEditStructureItem = (initialItem: FVIStructureItem, updatedItem: Partial<FVIStructureItem>) => {
        const matchFound = structureItems.filter(({ structureMemberName }) => structureMemberName !== initialItem.structureMemberName)
        .find(({ structureMemberName }) => matchPhrases(structureMemberName, updatedItem.structureMemberName));

        if (matchFound) {
            return toastService.error(`${updatedItem.structureMemberName} has already been added!`);
        }

        const newStructureItems = structureItems.map((equipmentItem) => equipmentItem.structureMemberName === initialItem.structureMemberName ? ({
            ...equipmentItem,
            hasAnomalies: updatedItem.hasAnomalies!,
            inspectionDescription: updatedItem.inspectionDescription!,
            structureMemberName: updatedItem.structureMemberName!,
            ...(enableComponentInspectionDetails ? { location: updatedItem.location ?? '' } : {}),
        }) : equipmentItem)

        onItemsChange(newStructureItems);
    };

    const handleSetStructureInspectionTypeEntries = (structureMemberName: string, entries: StructureInspectionTypeEntry[]) => {
        const newStructureItems = structureItems.map((item) =>
            item.structureMemberName === structureMemberName ? { ...item, inspectionTypeEntries: entries } : item
        );

        onItemsChange(newStructureItems);
    };

    const handleSetStructureHammerData = (structureMemberName: string, hammerTestData: ConcreteHammerData) => {
        const newStructureItems = structureItems.map((item) =>
            item.structureMemberName === structureMemberName ? { ...item, hammerTestData } : item
        );

        onItemsChange(newStructureItems);
    };

    const handleSetStructureUTMData = (structureMemberName: string, utmData: StructureUTMData) => {
        const newStructureItems = structureItems.map((item) =>
            item.structureMemberName === structureMemberName ? { ...item, utmData } : item
        );

        onItemsChange(newStructureItems);
    };

    const handleDeleteStructureItem = (structureMemberName: string): void => {
        const newStructureItems = structureItems.filter((item) => item.structureMemberName !== structureMemberName);
        onItemsChange(newStructureItems);
    };

    const handleSetStructureAnomalyItems = (structureMemberName: string, items: NewAnomalyItem[]) => {
        const newStructureItems = structureItems.map((item) =>
            item.structureMemberName === structureMemberName
                ? {
                        id: item.id,
                        structureMemberName: item.structureMemberName,
                        anomalyItems: items,
                        inspectionDescription: item.inspectionDescription,
                        hasAnomalies: item.hasAnomalies,
                        generalPhotos: item.generalPhotos,
                        generalDrawing: item.generalDrawing,
                    }
                : item
        );

        onItemsChange(newStructureItems);
    };

    const handleSetStructureGeneralPhotos = (structureMemberName: string, images: PDFImageProps[]) => {
        const newStructureItems = structureItems.map((item) =>
            item.structureMemberName === structureMemberName
                ? {
                        id: item.id,
                        structureMemberName: item.structureMemberName,
                        anomalyItems: item.anomalyItems,
                        inspectionDescription: item.inspectionDescription,
                        hasAnomalies: item.hasAnomalies,
                        generalDrawing: item.generalDrawing,
                        generalPhotos: images,
                    }
                : item
        );

        onItemsChange(newStructureItems);
    };

    const handleSetStructureGeneralDrawing = (structureMemberName: string, images: PDFImageProps[]) => {
        const newStructureItems = structureItems.map((item) =>
            item.structureMemberName === structureMemberName
                ? {
                        id: item.id,
                        structureMemberName: item.structureMemberName,
                        anomalyItems: item.anomalyItems,
                        inspectionDescription: item.inspectionDescription,
                        hasAnomalies: item.hasAnomalies,
                        generalDrawing: images,
                        generalPhotos: item.generalPhotos,
                    }
                : item
        );

        onItemsChange(newStructureItems);
    };

    const mappedAnomalyItems = useMappedAnomalies(structureItems);

    const someChecklistMissing = useMemo(() => {
		return getSomeChecklistItemsMissing(checklistItems, mappedAnomalyItems);
	}, [checklistItems, mappedAnomalyItems]);

    const isPDOChecklistComplete = useMemo(() => {
        return pdoChecklistItems.every((checklist) => checklist.value);
    }, [pdoChecklistItems]);

    useAnomalyRecords(structureItems.flatMap((structure) => structure.anomalyItems));
    useInspectedItemIDUpdate(structureItems.map((item) => item.structureMemberName));

    return ( 
        <React.Fragment>
			<Zippy headerTitle="P & ID" isValid={matchPhrases(pidData.hasPID, 'yes') ? pidData.pIDImages.length > 0 : true}>
				<FVIStructurePIDImageForm initialPIDData={pidData} onAdd={onChangePIDData} />
			</Zippy>

			<Zippy headerTitle='Checklist Items' isValid={!someChecklistMissing}>
                <Conditional isVisible={!!someChecklistMissing}>
                    <WarningNotice 
                        title='Checklist Incomplete' 
                        body={someChecklistMissing} 
                    />
                </Conditional>

				<ChecklistForm
					enableChecklistItemComment
					label={checklistLabel}
					additionalComment={additionalComment}
					anomalyItems={mappedAnomalyItems}
					lineNumber=''
					items={checklistItems}
					onAddComment={(comment: string) => onChangeAdditionalComment(comment)}
					onItemChange={(items: ChecklistItem[]) => onChangeChecklistItems(items)}
				/>
			</Zippy>

            <Zippy isValid={isPDOChecklistComplete} headerTitle="Potential Drop Object Checklist">
                <Conditional isVisible={!isPDOChecklistComplete}>
                    <WarningNotice 
                        title='PDO Checklist Incomplete' 
                        body='Please complete the PDO Checklist Section' 
                    />
                </Conditional>

                <PDOInclusionForm  initialItem={{ hasPDO }} onSave={(data) => onChangeHasPDO(data.hasPDO)} />

                <div className="divider my-4"></div>

                <FVIPDOChecklistForm
                    hasPDO={matchPhrases(hasPDO, 'yes')}
                    label="Structure Member Name"
                    value=""
                    items={pdoChecklistItems}
                    onChangeItems={(items) => onChangePDOChecklistItems(items)}
                />
            </Zippy>

            <Zippy headerTitle="Inspected Items" isValid={structureItems.length > 0}>
                <ItemCount count={structureItems.length} label='Inspected Structural Items' />

                <Conditional isVisible={isLastInspectedItemComplete && structureItems.length <= MAX_INSPECTED_ITEMS_PER_REPORT}>
                    <CustomNewItem label='Add New Structural Item'>
                        <StructuralMemberForm enableComponentInspectionDetails={enableComponentInspectionDetails} onSave={handleAddStructureItem} />
                    </CustomNewItem>
                </Conditional>

                <Conditional isVisible={!isLastInspectedItemComplete}>
                    <WarningNotice 
                        title='Structural Information Incomplete' 
                        body={`Please complete sections ${incompleteSections.join(', ')}`} 
                    />
                </Conditional>

                {structureItems.map((item, index) => (
                    <ExistingStructureItem
                        key={item.structureMemberName}
                        isPDOFormat={matchPhrases(hasPDO, 'yes')}
                        structureItem={item}
                        isOpenOnDefault={index === (structureItems.length - 1)}
                        nextAnomalyID={getAnomalyStartNumber(structureItems)}
                        enableComponentInspectionDetails={enableComponentInspectionDetails}
                        onEditStructureItem={handleEditStructureItem}
                        onDeleteStructureItem={handleDeleteStructureItem}
                        onChangeStructureAnomaly={handleSetStructureAnomalyItems}
                        onChangeGeneralDrawing={handleSetStructureGeneralDrawing}
                        onChangeGeneralPhotos={handleSetStructureGeneralPhotos}
                        onChangeInspectionTypeEntries={handleSetStructureInspectionTypeEntries}
                        onChangeHammerTestData={handleSetStructureHammerData}
                        onChangeUTMData={handleSetStructureUTMData}
                    />
                ))}
            </Zippy>

            <Zippy headerTitle="Anomaly Locations" isValid={matchPhrases(anomalyLocationData.hasAnomalyLocations, 'yes') ? anomalyLocationData.anomalyLocations.length > 0 : true}>
				<FVIStructureAnomalyLocations
					initialAnomalyLocations={anomalyLocationData} 
					onAdd={onChangeAnomalyLocationData} 
				/>
			</Zippy>

            <Zippy headerTitle="General Photos" isValid={generalPhotos.length > 0}>
                <VIPhotoRecordForm
                    initialGeneralPhotos={generalPhotos}
                    onProceed={(images: PDFImageProps[]) => onChangeGeneralPhotos(images)} 
                />
            </Zippy>
        </React.Fragment>
     );
};

export default StructureItems;